import React from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { SystemPage } from '@parsina/framework';
import { Ban, CircleDashed, Mail, Wrench } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useClinicContext, refOf } from '../../context/ClinicContext.jsx';
import { useAccess } from '../../hooks/useAccess.js';
import { mayOpenPath, landingPath } from '../../constants/nav.js';
import { stripClinic } from '../../hooks/useAppNavigate.js';
import MyAccessDrawer from './MyAccessDrawer.jsx';
import { skeletonForPath } from '../../screens/skeletons.jsx';

/**
 * The gate every clinic-scoped route passes through.
 *
 * Four questions, in this order, and the ORDER is the design:
 *
 *   1. Is the clinic in the URL one this person belongs to?   → named 403
 *   2. Can it be entered right now?                            → the specific reason
 *   3. Does it still need its setup finished?                  → repair, not a wall
 *   4. May they open THIS screen?                              → named 403
 *
 * Every failure names the clinic and, where it exists, the way out. A bare "access
 * denied" is the answer that generates a support call; «دسترسی شما در کلینیک مهر
 * معلق شده است» plus a button to their other clinic does not.
 *
 * Every state below is the kit's `SystemPage` — one shape for every fault,
 * which is that component's own argument and the reason the app's local
 * `PageState` stand-in could be deleted the moment the framework pin caught
 * up. The emoji glyphs it carried («⛔ ✉ ⚙ ◔») went with it: an emoji renders
 * as a different picture on every operating system and is not tinted by
 * `tone`, so the disc's colour and its glyph used to disagree.
 *
 * NOTHING HERE REDIRECTS SILENTLY on a permission failure. A silent bounce reads as
 * a broken link — the person clicked something and landed somewhere else with no
 * explanation, and will click it again. The one exception is a bare `/c/:ref/`,
 * which is not a screen at all and legitimately resolves to the first one they can
 * open (§5.8: the landing route is derived, never hardcoded to `/reception`).
 */
export default function ClinicGate({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const access = useAccess();
  const {
    activeMembership, activeRef, enterable, memberships, repairSetup, defaultRef,
  } = useClinicContext();

  const [repairing, setRepairing] = React.useState(false);
  const [repairError, setRepairError] = React.useState('');
  const [accessOpen, setAccessOpen] = React.useState(false);

  const goElsewhere = enterable.find((m) => refOf(m) !== activeRef);
  const elsewhereAction = goElsewhere
    ? {
      label: t('clinic.gate.goToOther', { name: goElsewhere.clinicName }),
      onClick: () => navigate(`/c/${refOf(goElsewhere)}/`),
    }
    : { label: t('clinic.gate.goToStart'), onClick: () => navigate('/start') };

  // ── 1. Not a member ───────────────────────────────────────────────────────
  if (!activeMembership) {
    return (
      <SystemPage
        tone="warning"
        icon={<Ban size={26} aria-hidden="true" />}
        title={t('clinic.gate.notMemberTitle')}
        body={t('clinic.gate.notMemberBody')}
        actions={[{ key: 'elsewhere', ...elsewhereAction }]}
        detail={activeRef}
        testId="gate-not-member"
      />
    );
  }

  // ── 2. Cannot be entered — say WHICH reason ───────────────────────────────
  if (!activeMembership.enterable) {
    const reason = activeMembership.blockedReason;
    const isPending = reason === 'INVITED' || reason === 'REQUESTED';
    return (
      <SystemPage
        tone={reason === 'SUSPENDED' ? 'warning' : 'info'}
        icon={isPending ? <Mail size={26} aria-hidden="true" /> : <Ban size={26} aria-hidden="true" />}
        title={t(`clinic.gate.blocked.${reason}.title`, { name: activeMembership.clinicName })}
        body={t(`clinic.gate.blocked.${reason}.body`, { name: activeMembership.clinicName })}
        testId={`gate-blocked-${reason}`}
        actions={isPending
          ? [
            { key: 'invitations', label: t('clinic.context.invitations'), onClick: () => navigate('/invitations') },
            { key: 'elsewhere', variant: 'outline', ...elsewhereAction },
          ]
          : [{ key: 'elsewhere', ...elsewhereAction }]}
      />
    );
  }

  // ── 3. Setup never finished ───────────────────────────────────────────────
  // The founder owns this clinic; they are not blocked, they are one idempotent
  // mutation away from working. Offering "create a clinic" here — which is what a
  // generic empty state would do — is the move that collides on clinics_pkey and
  // makes the situation permanent.
  if (activeMembership.repairNeeded) {
    return (
      <SystemPage
        tone="warning"
        icon={<Wrench size={26} aria-hidden="true" />}
        title={t('clinic.gate.repairTitle', { name: activeMembership.clinicName })}
        body={t('clinic.gate.repairBody')}
        detail={repairError}
        testId="gate-repair"
        actions={[{
          key: 'repair',
          label: repairing ? t('clinic.gate.repairing') : t('clinic.gate.repairAction'),
          onClick: async () => {
            setRepairing(true); setRepairError('');
            try {
              await repairSetup(activeMembership.clinicId);
            } catch (err) {
              setRepairError(err.message || '');
            } finally {
              setRepairing(false);
            }
          },
        }]}
      />
    );
  }

  // Access is resolved server-side; until it arrives, render no VERDICT. Showing
  // an empty sidebar for one frame is indistinguishable from «you have no
  // permissions», which is a claim we are not yet entitled to make. What IS
  // known is the page the URL names, so this draws that page's own skeleton —
  // the shell around it is already painted, and a spinner here was the one
  // moment in every navigation where the panel forgot which page it was on
  // (DESIGN.md §"Loading & skeleton").
  if (!access.ready) {
    return skeletonForPath(location.pathname, location.search) || (
      <div role="status" aria-busy="true" aria-label={t('common.state.loading')} data-state="loading" style={{ minHeight: 240 }} />
    );
  }

  const path = stripClinic(location.pathname);

  // ── 4a. A bare clinic root resolves to the first screen they can open ─────
  if (path === '/' || path === '') {
    const landing = landingPath(access);
    if (landing) return <Navigate to={`/c/${activeRef}${landing}`} replace />;
  }

  // ── 4b. Enterable, but holding nothing at all ─────────────────────────────
  // A real state, and common: a person added before their roles were assigned. An
  // empty rail and a blank page is not an acceptable rendering of it — their own
  // admin can already see it as ISSUE.NO_ROLES on «همکاران».
  if (access.noPermissions || !landingPath(access)) {
    // A practitioner here whose STAFF invitation is still open has no staff grants
    // until they accept it: the role has been offered, the missing step is theirs —
    // say so and lead them to it, not «the admin can assign your role» (PM-0536).
    const invited = activeMembership.staffStatus === 'INVITED';
    return (
      <>
        <SystemPage
          tone="info"
          icon={<CircleDashed size={26} aria-hidden="true" />}
          title={t(invited ? 'clinic.gate.invitedTitle' : 'clinic.gate.noRolesTitle', { name: activeMembership.clinicName })}
          body={t(invited ? 'clinic.gate.invitedBody' : 'clinic.gate.noRolesBody')}
          testId="gate-no-roles"
          actions={[
            ...(invited
              ? [{ key: 'invitations', label: t('clinic.context.invitations'), onClick: () => navigate('/invitations') }]
              : []),
            ...(goElsewhere ? [{ key: 'elsewhere', ...elsewhereAction }] : []),
            { key: 'access', variant: 'outline', label: t('clinic.myAccess.link'), onClick: () => setAccessOpen(true) },
          ]}
        />
        <MyAccessDrawer open={accessOpen} onClose={() => setAccessOpen(false)} />
      </>
    );
  }

  // ── 4c. This screen, specifically ─────────────────────────────────────────
  if (!mayOpenPath(access, path)) {
    const roleName = access.roles?.[0]?.nameFa || t(`clinic.context.tier.${String(access.tier || '').toLowerCase()}`);
    return (
      <>
        <SystemPage
          tone="warning"
          icon={<Ban size={26} aria-hidden="true" />}
          title={t('clinic.gate.noPermissionTitle')}
          body={t('clinic.gate.noPermissionBody', {
            clinic: activeMembership.clinicName,
            role: roleName,
          })}
          testId="gate-no-permission"
          actions={[
            {
              key: 'landing',
              label: t('clinic.gate.backToLanding'),
              onClick: () => navigate(`/c/${activeRef}${landingPath(access)}`),
            },
            { key: 'access', variant: 'outline', label: t('clinic.myAccess.link'), onClick: () => setAccessOpen(true) },
          ]}
        />
        <MyAccessDrawer open={accessOpen} onClose={() => setAccessOpen(false)} />
      </>
    );
  }

  return children;
}
